import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import numeral from 'numeral';
import PoppaysLoader from '@/components/poppays-loader';
import useParams from '@/hooks/use-params';
import formatUtcCalendarDate from '@/utils/formatUtcCalendarDate';
import translate from '@/utils/translate';
import GigLoadError from '../components/GigLoadError';
import GigShell from '../shell/GigShell';
import {GigTab} from '../shell/gigTabs';
import useGigShellData from '../shell/useGigShellData';
import {captureDeliverablesError, trackDeliverables} from '../telemetry';
import ProposalRow from './ProposalRow';
import styles from './GigApplicationPage.module.scss';

// `Do` (1st, 2nd, …) needs the advancedFormat plugin, matching Ember's moment format.
dayjs.extend(advancedFormat);

function formatDate(value: string | null, format: string): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : null;
}

/**
 * Ember rendered an application status pill here; the shell's sidebar already shows
 * that same state text on every tab, so it is deliberately not repeated.
 */
export default function GigApplicationPage() {
  const {gigId} = useParams();
  const navigate = useNavigate();

  const {
    shell,
    application,
    deliverables,
    totals,
    error,
    gigLoading,
    gigFailed,
    submissionsLoading,
    submissionsFailed,
    refetch,
  } = useGigShellData(gigId);

  useEffect(() => {
    trackDeliverables('Opened your application', {gigId});
  }, [gigId]);

  useEffect(() => {
    if (gigFailed || submissionsFailed) {
      captureDeliverablesError(error, 'Your application failed to load', {
        gigId,
        gigFailed,
        submissionsFailed,
      });
    }
  }, [error, gigFailed, submissionsFailed, gigId]);

  if (gigLoading) {
    return <PoppaysLoader />;
  }

  if (gigFailed) {
    return <GigLoadError onRetry={refetch} />;
  }

  const handleSelectKind = (tab: GigTab) => {
    if (tab.to) {
      navigate(tab.to);
    }
  };

  const appliedOn = formatDate(application.createdAt, 'MMMM Do, YYYY');
  const deadline = formatUtcCalendarDate(application.deadline, 'MM/DD/YYYY');
  const paymentDueOn = formatDate(application.paymentDueOn, 'MMMM D, YYYY');
  const {usagePeriodMonths} = application;

  return (
    <GigShell
      gigId={gigId}
      activeKind="application"
      shell={shell}
      deliverables={deliverables}
      totals={totals}
      showProgress={!submissionsLoading && !submissionsFailed}
      onSelectKind={handleSelectKind}
    >
      <div className={styles.page}>
        <header className={styles.contentHead}>
          <h1 className={styles.title}>{translate('Your Application')}</h1>
          {appliedOn ? (
            <p className={styles.appliedOn} data-testid="application-date">
              {translate('Application date:')} <span>{appliedOn}</span>
            </p>
          ) : null}
        </header>

        <div className={styles.rows}>
          {deadline ? (
            <ProposalRow
              testID="application-deadline"
              title={translate('Upload content by')}
              value={deadline}
            />
          ) : null}

          <ProposalRow
            testID="application-cost"
            title={translate('What will I be paid for this Gig?')}
            value={numeral(application.cost ?? 0).format('$0,0.00')}
          />

          {usagePeriodMonths ? (
            <ProposalRow
              testID="application-usage-period"
              title={translate('Usage Rights Period')}
              value={translate(
                usagePeriodMonths === 1 ? '{count} month' : '{count} months',
                {count: usagePeriodMonths}
              )}
            />
          ) : null}

          {application.answers.map((answer) => (
            <ProposalRow
              key={answer.id}
              testID="application-answer"
              title={answer.questionHtml}
              titleHtml
              value={answer.value}
            />
          ))}

          {paymentDueOn ? (
            <ProposalRow
              testID="application-payment"
              title={translate('Gig Status: Complete')}
              value={translate('Your payment should be received by {date}', {
                date: paymentDueOn,
              })}
            />
          ) : null}
        </div>
      </div>
    </GigShell>
  );
}
